'use client';

import { useEffect, useState } from 'react';
import {
  THEME_DEFAULT_VERSION,
  THEME_MIGRATION_KEY,
  THEME_STORAGE_KEY,
  resolveTheme,
  type Theme,
} from './useThemePreference';

export type { Theme } from './useThemePreference';

/**
 * Light/dark with localStorage persistence; applies `.light`/`.dark` to the
 * document root. Light is the universal default — the versioned migration
 * selects it once for existing sessions too, after which an explicit choice
 * persists. See `useThemePreference` for why this is shared and not per-app.
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'light';
    try {
      const migratedVersion = localStorage.getItem(THEME_MIGRATION_KEY);
      const resolved = resolveTheme(localStorage.getItem(THEME_STORAGE_KEY), migratedVersion);
      if (migratedVersion !== THEME_DEFAULT_VERSION) {
        localStorage.setItem(THEME_STORAGE_KEY, resolved);
        localStorage.setItem(THEME_MIGRATION_KEY, THEME_DEFAULT_VERSION);
      }
      return resolved;
    } catch {
      return 'light';
    }
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
      localStorage.setItem(THEME_MIGRATION_KEY, THEME_DEFAULT_VERSION);
    } catch {
      // Storage can be unavailable in hardened/private browser contexts. The
      // current document still changes theme; only persistence drops.
    }
  };

  return { theme, setTheme, isDark: theme === 'dark' };
}
